import {Autocomplete, TextField as MuiTextField} from "@mui/material";
import {SyntheticEvent} from "react";
import TextField from "../textfield/TextField";
import {SelectOption} from "./SelectOption";

export interface SingleSelectFieldProps<T> {
    id?: string;
    label: string;
    value?: T | null;
    onChange?: (value: T | null) => void;

    options: T[];
    getOptionLabel: (option: T) => string;
    renderOptionComponent?: (option: T, label: string) => JSX.Element;
    isOptionEqualToValue?: (option: T, value: T | null) => boolean;
    groupBy?: (option: T) => string;

    required?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    errorMessage?: string;
    helperText?: string;
    acceptFreeText?: T extends string ? boolean : false;
    limitTags?: number;
}

export default function SingleSelectField<T>(props: Readonly<SingleSelectFieldProps<T>>) {
    const {id, label, value, onChange, options, getOptionLabel, renderOptionComponent, groupBy, helperText} = props;
    const {required, readOnly, disabled, errorMessage, limitTags} = props;

    const optionValues = options;

    const acceptFreeText = props.acceptFreeText;

    function handleOnChange(event: SyntheticEvent, value: T | string | null) {
        onChange?.(value as T | null);
    }

    function formatOption(option: T | string): string {
        if (acceptFreeText && typeof option === 'string') {
            return option;
        } else {
            return getOptionLabel(option as T);
        }
    }

    if (readOnly) {
        return (
                <TextField id={id}
                    label={label}
                    value={value && formatOption(value)}
                    required={required}
                    readOnly={readOnly}
                    disabled={disabled}
                    helperText={helperText} />
        );
    }

    return (
        <Autocomplete id={id}
                multiple={false}
                value={value ?? null}
                onChange={handleOnChange}
                options={optionValues}
                getOptionLabel={formatOption}
                isOptionEqualToValue={props.isOptionEqualToValue}
                groupBy={groupBy}
                freeSolo={props.acceptFreeText}
                autoSelect={props.acceptFreeText}
                readOnly={readOnly}
                disabled={disabled}
                disableClearable={(required || readOnly)}
                limitTags={limitTags}
                size={"small"}
                renderOption={(props, option, {selected}) => (
                    <li {...props}>
                        <SelectOption option={option} label={formatOption(option)} checked={selected} renderOptionComponent={renderOptionComponent} />
                    </li>
                )}
                renderInput={(params) =>
                    <MuiTextField {...params}
                            label={label}
                            required={required}
                            error={errorMessage !== undefined}
                            helperText={errorMessage ?? helperText}
                            InputLabelProps={{shrink: true}}/>
                }/>
    );
}
